import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, SparklesIcon, ClipboardListIcon, CalendarDaysIcon, Users2Icon, BarChart3Icon, FileTextIcon, MenuIcon, XIcon, ZapIcon, TargetIcon, ClipboardCheckIcon, HeartPulseIcon, MessageSquareQuoteIcon, NetworkIcon, GraduationCapIcon, PieChartIcon, CheckIcon, XCircleIcon, Wand2Icon, QuoteIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
export function ForMICEPlannersPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const nav = [{
    label: 'For Planners',
    href: '#hero'
  }, {
    label: 'AI Designer',
    href: '#designer'
  }, {
    label: 'Event Hub',
    href: '#features'
  }, {
    label: 'Reports',
    href: '#solution'
  }, {
    label: 'Pricing',
    href: '#pricing'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-3926">
      {/* Header */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3927">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-3928">
          <Link to="/" data-id="element-3929">
            <Logo data-id="element-3930" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-3931">
            <ul className="flex items-center gap-7" data-id="element-3932">
              {nav.map(n => <li key={n.label} data-id="element-3933">
                  <a href={n.href} className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-3934">
                    {n.label}
                  </a>
                </li>)}
            </ul>
            <Button variant="primary" href="/designer" data-id="element-3935">
              Design Free Offsite
            </Button>
          </nav>
          <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-id="element-3936">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-3937" /> : <MenuIcon className="w-6 h-6" data-id="element-3938" />}
          </button>
        </div>

        {mobileOpen && <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 flex flex-col gap-3 shadow-md" data-id="element-3939">
            {nav.map(n => <a key={n.label} href={n.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-slate-700 py-2 border-b border-slate-50" data-id="element-3940">
                {n.label}
              </a>)}
            <Button variant="primary" href="/designer" className="mt-2 w-full" data-id="element-3941">
              Design Free Offsite
            </Button>
          </div>}
      </header>

      {/* HERO */}
      <section id="hero" className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden bg-white" data-id="element-3942">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
        backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
      }} data-id="element-3943" />

        {/* Ambient blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-3944">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.06] rounded-full blur-3xl" data-id="element-3945" />
          <motion.div animate={{
          x: [0, -25, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.05] rounded-full blur-3xl" data-id="element-3946" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3947">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-3948">
            {/* Copy */}
            <div className="lg:col-span-6 max-w-2xl" data-id="element-3949">
              <FadeIn data-id="element-3950">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm relative overflow-hidden" data-id="element-3951">
                  <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider relative z-10" data-id="element-3952">
                    For Planners
                  </span>
                  <span className="text-sm font-medium text-slate-600 relative z-10" data-id="element-3953">
                    Built for MICE & corporate event teams
                  </span>
                  <motion.span aria-hidden initial={{
                  x: '-120%'
                }} animate={{
                  x: '320%'
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: 'easeInOut'
                }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent skew-x-12 pointer-events-none" data-id="element-3954" />
                </div>
              </FadeIn>

              <FadeIn delay={0.05} data-id="element-3955">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6" data-id="element-3956">
                  Plan smarter offsites without adding{' '}
                  <span className="relative inline-block whitespace-nowrap" data-id="element-3957">
                    <span className="relative z-10 text-brand-navy" data-id="element-3958">
                      more tools
                    </span>
                    <motion.span initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: 1
                  }} transition={{
                    duration: 0.9,
                    delay: 0.6,
                    ease: [0.65, 0, 0.35, 1]
                  }} style={{
                    transformOrigin: 'left'
                  }} className="absolute bottom-1 left-0 right-0 h-3 bg-brand-coral/20 -z-0" data-id="element-3959" />
                  </span>{' '}
                  to manage.
                </h1>
              </FadeIn>

              <FadeIn delay={0.15} data-id="element-3960">
                <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed text-balance max-w-xl" data-id="element-3961">
                  Offsite+ helps MICE planners create structured, engaging,
                  measurable meetings and retreats — with AI-generated agendas,
                  attendee intake, group matching, learning prompts, wellness
                  preferences, and client-ready reporting.
                </p>
              </FadeIn>

              <FadeIn delay={0.25} data-id="element-3962">
                <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-3963">
                  <Button variant="primary" size="lg" href="/designer" className="group" data-id="element-3964">
                    Design Your Free Offsite
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3965" />
                  </Button>
                  <Button variant="outline" size="lg" data-id="element-3966">
                    Book Planner Demo
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.35} data-id="element-3967">
                <div className="border-t border-slate-100 pt-8 grid grid-cols-3 gap-6 max-w-md" data-id="element-3968">
                  <div data-id="element-3969">
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-3970">5×</div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5" data-id="element-3971">
                      Faster planning
                    </div>
                  </div>
                  <div data-id="element-3972">
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-3973">
                      92%
                    </div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5" data-id="element-3974">
                      Avg. intake rate
                    </div>
                  </div>
                  <div data-id="element-3975">
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-3976">
                      1 day
                    </div>
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5" data-id="element-3977">
                      To client report
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Planner Dashboard Mockup */}
            <FadeIn delay={0.4} className="lg:col-span-6 relative lg:h-[640px] flex items-center justify-center" data-id="element-3978">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10 rounded-full blur-3xl" data-id="element-3979" />

              {/* Decorative orbital ring */}
              <motion.div aria-hidden animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="hidden lg:block absolute w-[520px] h-[520px] rounded-full border border-dashed border-slate-200/80" data-id="element-3980">
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_0_4px_rgba(14,165,164,0.15)]" data-id="element-3981" />
                <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-brand-coral" data-id="element-3982" />
              </motion.div>

              {/* Floating accent — top left */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: -10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.1,
              duration: 0.6
            }} className="hidden md:block absolute top-8 -left-2 lg:left-4 z-20" data-id="element-3983">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white rounded-xl shadow-xl shadow-slate-300/40 border border-slate-100 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3984">
                  <div className="bg-emerald-50 p-1.5 rounded-md" data-id="element-3985">
                    <ZapIcon className="w-4 h-4 text-emerald-600" data-id="element-3986" />
                  </div>
                  <div data-id="element-3987">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3988">
                      Planning Speed
                    </div>
                    <div className="text-sm font-bold text-brand-navy" data-id="element-3989">
                      5× Faster
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating accent — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.3,
              duration: 0.6
            }} className="hidden md:block absolute bottom-10 -right-2 lg:right-4 z-20" data-id="element-3990">
                <motion.div animate={{
                y: [0, 8, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-brand-navy text-white rounded-xl shadow-xl shadow-slate-900/20 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3991">
                  <div className="bg-brand-teal/20 p-1.5 rounded-md" data-id="element-3992">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-300" data-id="element-3993" />
                  </div>
                  <div data-id="element-3994">
                    <div className="text-[10px] font-semibold text-teal-300 uppercase tracking-wider" data-id="element-3995">
                      Event Status
                    </div>
                    <div className="text-sm font-bold" data-id="element-3996">Setup 80%</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              y: 30,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} whileHover={{
              y: -6
            }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden z-10 transition-shadow hover:shadow-slate-400/40" data-id="element-3997">
                {/* Top bar */}
                <div className="bg-brand-navy text-white p-5 flex items-center justify-between relative overflow-hidden" data-id="element-3998">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-3999" />
                  <div className="relative z-10" data-id="element-4000">
                    <div className="text-xs text-slate-300 font-medium mb-1" data-id="element-4001">
                      Planner Dashboard
                    </div>
                    <h3 className="text-lg font-bold" data-id="element-4002">Q3 Sales Kickoff</h3>
                  </div>
                  <span className="bg-brand-teal/15 text-teal-300 text-xs font-semibold px-2 py-1 rounded-full relative z-10" data-id="element-4003">
                    Setup 80%
                  </span>
                </div>

                {/* Checklist */}
                <div className="p-5 bg-slate-50/50 space-y-3" data-id="element-4004">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-4005">
                    Event Setup Checklist
                  </div>

                  {[{
                  icon: ClipboardListIcon,
                  label: 'Attendee Intake',
                  status: 'Live',
                  done: true
                }, {
                  icon: CalendarDaysIcon,
                  label: 'Agenda Builder',
                  status: 'Drafted',
                  done: true
                }, {
                  icon: Users2Icon,
                  label: 'Group Matching',
                  status: 'Configured',
                  done: true
                }, {
                  icon: MessageSquareQuoteIcon,
                  label: 'Survey',
                  status: 'Scheduled',
                  done: true
                }, {
                  icon: BarChart3Icon,
                  label: 'Report Preview',
                  status: 'Pending',
                  done: false
                }].map(item => <div key={item.label} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-4006">
                      <div className="flex items-center gap-3" data-id="element-4007">
                        <div className={`p-2 rounded-md ${item.done ? 'bg-brand-teal/10 text-brand-teal' : 'bg-slate-100 text-slate-400'}`} data-id="element-4008">
                          <item.icon className="w-4 h-4" data-id="element-4009" />
                        </div>
                        <span className="text-sm font-medium text-slate-700" data-id="element-4010">
                          {item.label}
                        </span>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${item.done ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-100'}`} data-id="element-4011">
                        {item.done ? <span className="flex items-center" data-id="element-4012">
                            <CheckCircle2Icon className="w-3 h-3 mr-1" data-id="element-4013" />
                            {item.status}
                          </span> : item.status}
                      </span>
                    </div>)}

                  {/* Mini stat */}
                  <div className="grid grid-cols-2 gap-3 pt-2" data-id="element-4014">
                    <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-4015">
                      <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-4016">
                        Intake responses
                      </div>
                      <div className="flex items-baseline gap-1" data-id="element-4017">
                        <span className="text-xl font-bold text-brand-navy" data-id="element-4018">
                          38
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-4019">/ 48</span>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-4020">
                      <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-4021">
                        Days to event
                      </div>
                      <div className="flex items-baseline gap-1" data-id="element-4022">
                        <span className="text-xl font-bold text-brand-coral" data-id="element-4023">
                          12
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <Section className="bg-slate-50" data-id="element-4024">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-4025">
          <FadeIn data-id="element-4026">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-4027">
              Planning the logistics is only half the job.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-4028">
              Clients increasingly expect more from the events you run. Offsite+
              gives you a structured way to deliver it.
            </p>
          </FadeIn>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4029">
          {[{
          icon: TargetIcon,
          title: 'Clients want better engagement'
        }, {
          icon: ClipboardCheckIcon,
          title: 'Agendas need more structure'
        }, {
          icon: ClipboardListIcon,
          title: 'Attendee needs are scattered'
        }, {
          icon: NetworkIcon,
          title: 'Networking is hard to design'
        }, {
          icon: HeartPulseIcon,
          title: 'Wellness preferences are missed'
        }, {
          icon: FileTextIcon,
          title: 'Reporting takes too much time'
        }].map((p, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-4030">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 h-full shadow-sm" data-id="element-4031">
                <div className="bg-slate-100 p-3 rounded-xl inline-flex mb-4 text-brand-coral" data-id="element-4032">
                  <p.icon className="w-5 h-5" data-id="element-4033" />
                </div>
                <h3 className="text-base font-bold text-brand-navy" data-id="element-4034">
                  {p.title}
                </h3>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Before / After */}
      <Section className="bg-white" data-id="element-4035">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4036">
          <FadeIn data-id="element-4037">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4038">
              Stop wrangling spreadsheets. Start designing experiences.
            </h2>
          </FadeIn>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-4039">
          <FadeIn delay={0.1} data-id="element-4040">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 h-full" data-id="element-4041">
              <div className="flex items-center gap-3 mb-6" data-id="element-4042">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500" data-id="element-4043">
                  <XCircleIcon className="w-5 h-5" data-id="element-4044" />
                </div>
                <h3 className="text-xl font-bold text-slate-700" data-id="element-4045">
                  The Old Way
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-4046">
                {['Scattered tools and manual spreadsheets', 'Chasing attendees for dietary needs', 'Generic agendas built from scratch', 'Networking left to chance', 'No easy way to prove ROI to the client'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-600" data-id="element-4047">
                    <XIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-4048" />
                    <span data-id="element-4049">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-4050">
            <div className="bg-brand-navy rounded-2xl p-8 border border-brand-navy shadow-xl h-full relative overflow-hidden" data-id="element-4051">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl" data-id="element-4052" />
              <div className="relative z-10" data-id="element-4053">
                <div className="flex items-center gap-3 mb-6" data-id="element-4054">
                  <div className="w-10 h-10 rounded-full bg-brand-teal/20 flex items-center justify-center text-brand-teal" data-id="element-4055">
                    <CheckCircle2Icon className="w-5 h-5" data-id="element-4056" />
                  </div>
                  <h3 className="text-xl font-bold text-white" data-id="element-4057">
                    With Offsite+
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-4058">
                  {['One unified workspace for the whole event', 'Automated intake and preferences', 'AI-designed pathways and agendas', 'Smart group matching and prompts', 'Instant, executive-ready client reports'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-4059">
                      <CheckCircle2Icon className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-4060" />
                      <span data-id="element-4061">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Planner Workflow Swimlane */}
      <Section className="bg-slate-50 border-y border-slate-100" data-id="element-4062">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4063">
          <FadeIn data-id="element-4064">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4065">
              A seamless workflow from start to finish.
            </h2>
          </FadeIn>
        </div>
        <div className="max-w-5xl mx-auto" data-id="element-4066">
          <div className="flex flex-col md:flex-row gap-4 relative" data-id="element-4067">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2 z-0" data-id="element-4068" />
            {[{
            step: '1',
            title: 'Intake',
            desc: 'Collect goals & dietary needs automatically.',
            icon: ClipboardListIcon
          }, {
            step: '2',
            title: 'Agenda',
            desc: 'AI builds the perfect schedule in minutes.',
            icon: CalendarDaysIcon
          }, {
            step: '3',
            title: 'Hub',
            desc: 'Attendees access everything on their phones.',
            icon: SparklesIcon
          }, {
            step: '4',
            title: 'Report',
            desc: 'Generate a polished ROI summary instantly.',
            icon: PieChartIcon
          }].map((s, i) => <FadeIn key={s.step} delay={i * 0.1} className="flex-1 relative z-10" data-id="element-4069">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center h-full" data-id="element-4070">
                  <div className="w-12 h-12 rounded-full bg-brand-navy text-white flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-4071">
                    <s.icon className="w-5 h-5" data-id="element-4072" />
                  </div>
                  <h3 className="font-bold text-brand-navy mb-2" data-id="element-4073">{s.title}</h3>
                  <p className="text-sm text-slate-600" data-id="element-4074">{s.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Tools You Replace Grid */}
      <Section className="bg-white" data-id="element-4075">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4076">
          <FadeIn data-id="element-4077">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4078">
              Consolidate your event tech stack.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-4079">
              Replace the scattered tools you use today with one purpose-built
              platform.
            </p>
          </FadeIn>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4080">
          {[{
          tool: 'Typeform / Forms',
          replacedBy: 'Automated Intake'
        }, {
          tool: 'Slack / WhatsApp',
          replacedBy: 'Event Hub'
        }, {
          tool: 'Google Docs',
          replacedBy: 'AI Agenda Builder'
        }, {
          tool: 'SurveyMonkey',
          replacedBy: 'Integrated Surveys'
        }].map((t, i) => <FadeIn key={t.tool} delay={i * 0.1} data-id="element-4081">
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-center h-full flex flex-col justify-center" data-id="element-4082">
                <div className="text-sm text-slate-500 line-through mb-2" data-id="element-4083">
                  {t.tool}
                </div>
                <div className="font-bold text-brand-teal" data-id="element-4084">{t.replacedBy}</div>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* PLANNER VALUE */}
      <Section className="bg-slate-50" data-id="element-4085">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-4086">
          <FadeIn data-id="element-4087">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-4088">
              Designed to make planners look sharper.
            </h2>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-4089">
          {[{
          icon: ZapIcon,
          title: 'Build faster',
          desc: 'Generate agenda ideas, prompts, and activity structures in minutes.',
          color: 'bg-brand-teal/10 text-brand-teal'
        }, {
          icon: ClipboardCheckIcon,
          title: 'Collect better inputs',
          desc: 'Understand attendee goals, preferences, dietary needs, and comfort levels before arrival.',
          color: 'bg-brand-coral/10 text-brand-coral'
        }, {
          icon: SparklesIcon,
          title: 'Improve the experience',
          desc: 'Guide networking, wellness, learning, and reflection without making the event feel forced.',
          color: 'bg-brand-navy/10 text-brand-navy'
        }, {
          icon: BarChart3Icon,
          title: 'Report cleanly',
          desc: 'Send clients a polished summary of engagement, feedback, outcomes, and next steps.',
          color: 'bg-emerald-50 text-emerald-600'
        }].map((v, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-4090">
              <motion.div whileHover={{
            y: -4
          }} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm h-full transition-shadow hover:shadow-md" data-id="element-4091">
                <div className={`p-3 rounded-xl inline-flex mb-4 ${v.color}`} data-id="element-4092">
                  <v.icon className="w-5 h-5" data-id="element-4093" />
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2" data-id="element-4094">
                  {v.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4095">
                  {v.desc}
                </p>
              </motion.div>
            </FadeIn>)}
        </div>
      </Section>

      {/* AI DESIGNER */}
      <Section id="designer" className="bg-white" data-id="element-4096">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-4097">
          <FadeIn data-id="element-4098">
            <div className="inline-flex items-center gap-2 bg-brand-coral/10 text-brand-coral text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" data-id="element-4099">
              <Wand2Icon className="w-3.5 h-3.5" data-id="element-4100" /> AI Designer
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-4101">
              Start every proposal with a smarter blueprint.
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-4102">
              Answer six questions, get a structured agenda, recommended
              activities, and ready-to-use intake questions you can drop into
              any client proposal.
            </p>
            <Button variant="accent" size="lg" href="/designer" className="group" data-id="element-4103">
              Try the AI Designer
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4104" />
            </Button>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-4105">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-8" data-id="element-4106">
              <div className="grid grid-cols-2 gap-6" data-id="element-4107">
                <div data-id="element-4108">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-4109">
                    Inputs
                  </div>
                  <ul className="space-y-2" data-id="element-4110">
                    {['Event type', 'Audience', 'Goals', 'Location', 'Attendee count', 'Energy level'].map(x => <li key={x} className="flex items-center gap-2 text-sm text-slate-700 bg-white px-3 py-2 rounded-lg border border-slate-100" data-id="element-4111">
                        <CheckIcon className="w-3.5 h-3.5 text-brand-teal" data-id="element-4112" />{' '}
                        {x}
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-4113">
                  <div className="text-xs font-bold text-brand-coral uppercase tracking-wider mb-3" data-id="element-4114">
                    Outputs
                  </div>
                  <ul className="space-y-2" data-id="element-4115">
                    {['Agenda', 'Activities', 'Learning themes', 'Group formats', 'Intake questions', 'Reporting metrics'].map(x => <li key={x} className="flex items-center gap-2 text-sm font-semibold text-brand-navy bg-white px-3 py-2 rounded-lg border-2 border-brand-coral/20" data-id="element-4116">
                        <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-4117" />{' '}
                        {x}
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-between text-sm" data-id="element-4118">
                <span className="text-slate-500" data-id="element-4119">Avg. generation time</span>
                <span className="font-mono font-bold text-brand-teal" data-id="element-4120">
                  ~15s
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* World-Class Content & Frameworks */}
      <Section className="bg-slate-50 border-y border-slate-100" data-id="element-4121">
        <div className="max-w-5xl mx-auto text-center" data-id="element-4122">
          <FadeIn data-id="element-4123">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4" data-id="element-4124">
              World-class frameworks for your clients.
            </h2>
            <p className="text-slate-600 mb-10 max-w-2xl mx-auto" data-id="element-4125">
              Every Offsite+ pathway is built from licensed and adapted material
              from top business schools and bestselling authors.
            </p>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-4126">
            {['Harvard Business School', 'Wharton Executive Education', 'INSEAD', 'Stanford GSB'].map((src, i) => <FadeIn key={src} delay={i * 0.1} data-id="element-4127">
                <div className="font-serif text-lg md:text-xl text-brand-navy font-medium leading-tight" data-id="element-4128">
                  {src}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* FEATURES */}
      <Section id="features" className="bg-white" data-id="element-4129">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-4130">
          <FadeIn data-id="element-4131">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-4132">
              Everything in one event hub.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-4133">
              No new logins, no scattered spreadsheets — just a clean planner
              workspace from intake to report.
            </p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4" data-id="element-4134">
          {[{
          icon: CalendarDaysIcon,
          title: 'Agenda Builder'
        }, {
          icon: ClipboardListIcon,
          title: 'Attendee Intake'
        }, {
          icon: SparklesIcon,
          title: 'Event Hub'
        }, {
          icon: GraduationCapIcon,
          title: 'Learning Pathways'
        }, {
          icon: NetworkIcon,
          title: 'Group Matching'
        }, {
          icon: MessageSquareQuoteIcon,
          title: 'Reflection Prompts'
        }, {
          icon: HeartPulseIcon,
          title: 'Wellness Preferences'
        }, {
          icon: ClipboardCheckIcon,
          title: 'Surveys'
        }, {
          icon: PieChartIcon,
          title: 'Client Report'
        }].map((f, i) => <FadeIn key={i} delay={i * 0.04} data-id="element-4135">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 flex items-center gap-3 h-full hover:bg-slate-100 transition-colors" data-id="element-4136">
                <div className="bg-brand-navy/5 p-2.5 rounded-lg text-brand-navy" data-id="element-4137">
                  <f.icon className="w-5 h-5" data-id="element-4138" />
                </div>
                <h4 className="text-sm font-bold text-brand-navy" data-id="element-4139">{f.title}</h4>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="bg-slate-50" data-id="element-4140">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-4141">
          <FadeIn data-id="element-4142">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-4143">
              Pricing that scales with the event.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-4144">
              Start free with the AI Designer. Upgrade per event as you bring
              client work into the platform.
            </p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto" data-id="element-4145">
          {[{
          name: 'Free',
          price: '$0',
          desc: 'AI Designer',
          highlight: false,
          cta: 'Try Free',
          href: '/designer'
        }, {
          name: 'Starter',
          price: '$499',
          period: '/event',
          desc: 'Small offsites',
          highlight: false,
          cta: 'Start',
          href: '/designer'
        }, {
          name: 'Professional',
          price: '$1,500',
          period: '/event',
          desc: 'Recommended for planners',
          highlight: true,
          cta: 'Launch Event',
          href: '/designer'
        }, {
          name: 'Premium',
          price: '$3,500',
          period: '/event',
          desc: 'Premium retreats',
          highlight: false,
          cta: 'Build Premium',
          href: '/designer'
        }, {
          name: 'Partner',
          price: 'Custom',
          desc: 'Multi-event annual',
          highlight: false,
          cta: 'Contact Sales',
          href: '#'
        }].map(p => <div key={p.name} className={`relative rounded-2xl p-6 flex flex-col ${p.highlight ? 'border-2 border-brand-coral bg-white shadow-xl lg:-mt-4 lg:mb-4 z-10' : 'border border-slate-200 bg-white shadow-sm'} ${p.name === 'Partner' || p.name === 'Premium' ? 'col-span-2 lg:col-span-1' : ''}`} data-id="element-4146">
              {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-coral text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full" data-id="element-4147">
                  Recommended
                </span>}
              <h3 className="text-base font-bold text-brand-navy mb-1" data-id="element-4148">
                {p.name}
              </h3>
              <div className="flex items-baseline gap-1 mb-2" data-id="element-4149">
                <span className="text-2xl font-black text-slate-800" data-id="element-4150">
                  {p.price}
                </span>
                {p.period && <span className="text-xs text-slate-500 font-medium" data-id="element-4151">
                    {p.period}
                  </span>}
              </div>
              <p className="text-xs text-slate-500 mb-5 flex-1" data-id="element-4152">{p.desc}</p>
              <Button variant={p.highlight ? 'accent' : p.name === 'Free' ? 'outline' : 'primary'} size="sm" href={p.href} className="w-full" data-id="element-4153">
                {p.cta}
              </Button>
            </div>)}
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="bg-white" data-id="element-4154">
        <div className="max-w-4xl mx-auto text-center" data-id="element-4155">
          <FadeIn data-id="element-4156">
            <QuoteIcon className="w-12 h-12 text-brand-teal/20 mx-auto mb-6" data-id="element-4157" />
            <h2 className="text-2xl md:text-4xl font-bold text-brand-navy leading-tight mb-8" data-id="element-4158">
              "I used to spend weeks chasing dietary restrictions and formatting
              agendas. Now I spin up a branded event hub in 15 minutes and spend
              my time on the actual experience."
            </h2>
            <div className="flex items-center justify-center gap-4" data-id="element-4159">
              <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden" data-id="element-4160">
                <img src="https://i.pravatar.cc/150?img=32" alt="Avatar" className="w-full h-full object-cover" data-id="element-4161" />
              </div>
              <div className="text-left" data-id="element-4162">
                <div className="font-bold text-brand-navy" data-id="element-4163">Elena Rostova</div>
                <div className="text-sm text-slate-500" data-id="element-4164">
                  Senior Event Planner, Fortune 500
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="bg-brand-navy text-white relative overflow-hidden" data-id="element-4165">
        <div className="absolute inset-0 pointer-events-none" data-id="element-4166">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-brand-teal/20 rounded-full blur-[120px]" data-id="element-4167" />
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-brand-coral/20 rounded-full blur-[120px]" data-id="element-4168" />
        </div>

        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-4169">
          <FadeIn data-id="element-4170">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance text-white" data-id="element-4171">
              Make your next event easier to design and easier to prove.
            </h2>
            <p className="text-lg text-slate-300 mb-10 text-balance max-w-xl mx-auto" data-id="element-4172">
              Generate a free blueprint or get a tailored walkthrough for your
              upcoming client event.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-4173">
              <Button variant="accent" size="lg" href="/designer" data-id="element-4174">
                Design Free Offsite
              </Button>
              <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10 hover:border-white/40" data-id="element-4175">
                Book Planner Demo
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Mini footer */}
      <footer className="bg-white border-t border-slate-100 py-10" data-id="element-4176">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-4177">
          <Logo data-id="element-4178" />
          <div className="text-sm text-slate-400" data-id="element-4179">
            © {new Date().getFullYear()} Offsite+. Built for planners.
          </div>
          <div className="flex gap-6 text-sm font-medium text-slate-500" data-id="element-4180">
            <Link to="/" className="hover:text-brand-navy transition-colors" data-id="element-4181">
              Home
            </Link>
            <Link to="/for-hotels" className="hover:text-brand-navy transition-colors" data-id="element-4182">
              For Hotels
            </Link>
            <Link to="/for-dmcs" className="hover:text-brand-navy transition-colors" data-id="element-4183">
              For DMCs
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}